import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButton, MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { MatLegacySelectModule } from '@angular/material/legacy-select';
import { MatLegacyTableModule } from '@angular/material/legacy-table';
import { MatLegacyTooltipModule } from '@angular/material/legacy-tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableDataSource } from '@angular/material/table';
import { AngularSplitModule } from 'angular-split';
import _ from 'lodash';
import { Subject, debounceTime, distinctUntilChanged, fromEvent, merge, takeUntil, tap } from 'rxjs';
import { ComponentsModule } from '../../components/components.module';
import { ImageViewerComponent } from '../../components/image-viewer/image-viewer.component';
import { CuttingButt } from '../../models/cutting/cutting-butt.model';
import { CuttingMatBaseGroup } from '../../models/cutting/cutting-material-group.model';
import { CuttingButtFormService } from '../../services/cutting-butt-form.service';
import { LegacyDialogsService } from '../../services/legacy-dialogs.service';
import { ApiLinks } from '../cutting-panel-list/csl.api-links';
import { TextureViewerModule } from '../texture-viewer/texture-viewer.module';
import { MaterialBaseButtsService } from './material-base-butts.service';

@Component({
  selector: 'csl-material-base-butts',
  templateUrl: 'material-base-butts.component.html',
  styleUrls: ['material-base-butts.component.scss'],
  standalone: true,
  imports: [
    MatLegacyButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatDividerModule,
    MatLegacyFormFieldModule,
    MatLegacySelectModule,
    MatLegacyInputModule,
    MatLegacyTableModule,
    AngularSplitModule,
    MatLegacyTooltipModule,
    MatLegacyCheckboxModule,
    FormsModule,
    ComponentsModule, // only for tree
    NgClass,
    NgIf,
    NgFor,
    TextureViewerModule,
  ],
  providers: [
    {
      provide: MaterialBaseButtsService,
      useClass: CuttingButtFormService,
    },
  ],
})
export class MaterialBaseButtsComponent implements OnDestroy, OnInit, AfterViewInit {
  dataSource: MatTableDataSource<CuttingButt> = new MatTableDataSource();
  displayedColumns = ['select', 'texture', 'name', 'article', 'thickness', 'quantityInStock'];
  pageSize = 25;
  pageIndex = 0;
  pageIndex$: Subject<number> = new Subject<number>();
  loading = false;

  @HostBinding() class = 'flex flex-col overflow-hidden h-full';

  @ViewChild('filter', { static: true }) filter: ElementRef;
  @ViewChild('filterButton', { static: true }) filterButton: MatLegacyButton;

  @Input() dataProvider?: MaterialBaseButtsService;
  @Input() showQuantityInStock?: boolean;
  @Input() filterByThickness?: number;
  @Input() panelThickness?: number;

  @Output() selectionChange: EventEmitter<CuttingButt> = new EventEmitter();

  pathToTextures = '';
  buttsTotalCount = 0;
  selectedButt: CuttingButt = undefined;
  buttGroups: CuttingMatBaseGroup[] = [];
  currentGroupId = 0;
  focusedGroupId = -1;
  buttsThicknesses: number[] = [];
  unsubscribe$: Subject<void> = new Subject();

  constructor(
    public buttFormService: MaterialBaseButtsService,

    private dialogs: LegacyDialogsService
  ) {
    this.pageIndex$
      .pipe(
        tap(value => (this.pageIndex = value)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
    this.pathToTextures = ApiLinks.materialTexture;
  }

  onSelectGroup(group: CuttingMatBaseGroup): void {
    this.currentGroupId = group.id;
    this.dataSource.data = [];
    this.pageIndex$.next(0);
  }

  get hasMore(): boolean {
    return this.buttsTotalCount - this.dataSource.data.length > 0;
  }

  ngOnInit(): void {
    this.dataProvider ??= this.buttFormService;

    this.getButtGroups();
    this.getButtsThicknesses();

    if (!this.showQuantityInStock) {
      _.pull(this.displayedColumns, 'quantityInStock');
    }
  }

  ngAfterViewInit(): void {
    this.addListeners();
  }

  showFullNameTooltip(el: HTMLElement): boolean {
    if (el.offsetWidth < el.scrollWidth) {
      return true;
    } else {
      return false;
    }
  }

  onChangeFilterByThickness(event: any): void {
    this.dataSource.data = [];
    this.pageIndex$.next(0);
  }

  private addListeners(): void {
    const filterEvent = merge(
      fromEvent(this.filter.nativeElement, 'keyup'), //
      fromEvent(this.filterButton._elementRef.nativeElement, 'click')
    ).pipe(debounceTime(500), distinctUntilChanged());

    this.pageIndex$.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.getButts();
    });

    filterEvent.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      this.dataSource.data = [];
      this.pageIndex$.next(0);
    });
  }

  private getButts(): void {
    this.loading = true;
    this.filter.nativeElement.value = (this.filter.nativeElement.value as string).replace(/'/g, '');

    console.log(this.pageIndex);
    this.dataProvider
      .getButtsFromMatBase(
        this.currentGroupId,
        this.pageSize,
        this.pageIndex,
        this.filter.nativeElement.value,
        this.filterByThickness,
        this.panelThickness
      )
      .then(response => {
        this.loading = false;

        this.dataSource.data = [...this.dataSource.data, ...response.materialsDTO];

        this.buttsTotalCount = response.materialsCount;
      });
  }

  getButtGroups(): void {
    this.dataProvider.getButtGroupsFromMatBase().then(response => {
      this.buttGroups = response;

      if (response.length > 0) {
        this.focusedGroupId = response[0].id;
      }
    });
  }

  getButtsThicknesses(): void {
    this.dataProvider.getButtsThickneses().then(response => {
      this.buttsThicknesses = response;

      if (this.filterByThickness > 0) {
        const i = this.buttsThicknesses.indexOf(this.filterByThickness);
        if (i === -1) {
          this.buttsThicknesses.push(this.filterByThickness);
        }
      }
    });
  }

  setImagePlug(e: Event): void {
    (e.target as HTMLImageElement).src = '';
  }

  toggleSelection(item: CuttingButt): void {
    if (this.selectedButt === item) {
      this.selectedButt = undefined;
    } else {
      this.selectedButt = item;
    }

    this.selectionChange.emit(this.selectedButt);
  }

  isSelected(item: CuttingButt): boolean {
    if (!this.selectedButt) {
      return false;
    }

    return (
      this.selectedButt.name.toLowerCase().trim() === item.name.toLowerCase().trim() &&
      this.selectedButt.sign.toLowerCase().trim() === item.sign.toLowerCase().trim() &&
      this.selectedButt.article.toLowerCase().trim() === item.article.toLowerCase().trim()
    );
  }

  openTextureViewerDialog<ImageViewerData>(link: string): void {
    this.dialogs.open(ImageViewerComponent, {
      panelClass: 'image-viewer-dialog',
      disableClose: false,
      data: {
        textureLink: link,
      } as ImageViewerData,
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
